import React, { useCallback, useEffect, useRef, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import InputAdornment from "@material-ui/core/InputAdornment";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Select from "@material-ui/core/Select";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import SearchIcon from "@material-ui/icons/Search";

import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmEfetivarModal from "./ConfirmEfetivarModal";
import { parseContactConflict } from "../../utils/contactConflict";
import { classifyEfetivarResult } from "../../utils/efetivarResult";
import { classifyMatchPreview, formatIds, sanitizeIds } from "../../utils/matchPreview";

import { errorReasonLabel, statusColor, statusLabel } from "./helpers";
import { formatCurrency, formatDateOnly } from "../Opportunities/helpers";

const STATUS_FILTERS = [
  { value: "", label: "Todos" },
  { value: "pendente_conferencia", label: "Pendente conferência" },
  { value: "senha_pendente", label: "Senha pendente" },
  { value: "erro_leitura", label: "Erro de leitura" },
  { value: "efetivado", label: "Efetivado" },
  { value: "ignorado", label: "Ignorado" },
];

const COLUMNS = 9;

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  search: {
    minWidth: 320,
  },
  statusFilter: {
    minWidth: 200,
  },
  count: {
    marginLeft: "auto",
    color: theme.palette.text.secondary,
  },
  tableWrapper: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    ...theme.scrollbarStyles,
  },
  chip: {
    color: "#FFF",
    fontWeight: "bold",
  },
  reason: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  actions: {
    display: "flex",
    gap: 4,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  action: {
    fontSize: 11,
    padding: "2px 8px",
    textTransform: "none",
    whiteSpace: "nowrap",
  },
  passwordBox: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 4,
  },
  batchNotice: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderLeft: "3px solid #2E7D32",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

/**
 * Aba de conferência: a fila de documentos importados, com busca, filtro e
 * as ações de efetivar/ignorar/desbloquear.
 *
 * `batchFilter` chega da aba de importação quando o operador clica em
 * "Conferir documentos importados" — a lista abre já restrita ao lote que
 * ele acabou de enviar, e `onClearBatchFilter` solta essa restrição.
 */
const ConferenceTab = ({ batchFilter = "", onClearBatchFilter }) => {
  const classes = useStyles();

  const [imports, setImports] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // Ações em andamento, por id — evita clique duplo e dá feedback na linha.
  const [busyId, setBusyId] = useState(null);
  const [passwordDrafts, setPasswordDrafts] = useState({});
  // Documento aguardando confirmação no modal de efetivar.
  const [efetivarAlvo, setEfetivarAlvo] = useState(null);
  // Telefone digitado bate com contato que já tem nome real — a tela pergunta
  // antes de vincular. Enquanto está preenchido, o modal mostra a pergunta.
  const [conflito, setConflito] = useState(null);
  // Linhas que o backend avisou já estarem salvas (só nesta sessão/tela).
  const [jaSalvas, setJaSalvas] = useState({});
  // Ids efetivados com sucesso nesta sessão e ids com efetivar em andamento.
  const efetivadosRef = useRef(new Set());
  const emAndamentoRef = useRef(new Set());

  const fetchImports = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/policy-document-imports", {
        params: {
          pageNumber,
          searchParam: searchParam || undefined,
          status: statusFilter || undefined,
          importBatchId: batchFilter || undefined,
        },
      });
      setImports((previous) =>
        pageNumber === 1 ? data.imports : [...previous, ...data.imports]
      );
      if (pageNumber === 1) {
        setJaSalvas({});
        efetivadosRef.current.clear();
      }
      setCount(data.count);
      setHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [pageNumber, searchParam, statusFilter, batchFilter]);

  useEffect(() => {
    // Busca e filtro reiniciam a paginação; sem isto a página 3 de um filtro
    // antigo continuaria valendo para o filtro novo.
    const timeout = setTimeout(() => {
      fetchImports();
    }, 300);
    return () => clearTimeout(timeout);
  }, [fetchImports]);

  const handleSearchChange = (value) => {
    setPageNumber(1);
    setSearchParam(value);
  };

  const handleStatusChange = (value) => {
    setPageNumber(1);
    setStatusFilter(value);
  };

  /** Substitui a linha pelo que o backend devolveu, sem recarregar a lista
   * inteira — a posição do operador na tabela é preservada. */
  const replaceRow = (updated) => {
    setImports((previous) => previous.map((row) => (row.id === updated.id ? updated : row)));
  };

  const runAction = async (row, action, body) => {
    setBusyId(row.id);
    try {
      const { data } = await api.post(`/policy-document-imports/${row.id}/${action}`, body);
      replaceRow(data);
      return true;
    } catch (err) {
      toastError(err);
      return false;
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Efetivar de verdade. O 409 é resposta esperada, não erro: significa que o
   * telefone digitado já é de um contato com nome de gente e quem confere
   * precisa decidir. Nada foi gravado no servidor nesse caso.
   */
  const efetivarDocumento = async (alvo, body) => {
    // Impede segunda chamada concorrente para o mesmo documento.
    if (busyId === alvo.id || emAndamentoRef.current.has(alvo.id)) return;
    emAndamentoRef.current.add(alvo.id);
    setBusyId(alvo.id);
    try {
      const { data } = await api.post(`/policy-document-imports/${alvo.id}/efetivar`, body);
      const result = classifyEfetivarResult(data);
      if (result.kind === "ja_importada") {
        // Nada foi gravado. Se este id já foi efetivado com sucesso aqui, a
        // resposta é de um duplo clique: a linha já foi substituída, ignora.
        if (!efetivadosRef.current.has(alvo.id)) {
          setJaSalvas((previous) => ({ ...previous, [alvo.id]: true }));
          toast.info(i18n.t("policyImport.alreadyImported.message"));
        }
      } else {
        efetivadosRef.current.add(alvo.id);
        replaceRow(data);
        if (result.kind === "atualizou_cadastro") {
          toast.info(i18n.t("policyImport.updatedExisting.message"));
        }
        if (result.cardMessages) {
          result.cardMessages.forEach((key) => toast.info(i18n.t(key)));
        }
        const duplicateIds = sanitizeIds(data && data.possibleDuplicateIds);
        if (duplicateIds.length) {
          toast.warning(i18n.t("policyImport.possibleDuplicate.toast", { ids: formatIds(duplicateIds) }));
        }
      }
      setEfetivarAlvo(null);
      setConflito(null);
    } catch (err) {
      if (err?.response?.status === 409) {
        // Guarda o telefone junto: é ele que precisa voltar na confirmação,
        // e ele vive no estado do modal, não aqui.
        // Só reason, ids numéricos e nome vêm do corpo (parseContactConflict
        // descarta o resto); nada disso vai para log.
        const parsed = parseContactConflict(err.response.data);
        setConflito({
          reason: parsed?.reason || "desconhecido",
          contactIds: parsed?.contactIds || [],
          contactName: parsed?.contactName || "",
          phone: body?.phone
        });
      } else {
        toastError(err);
      }
    } finally {
      emAndamentoRef.current.delete(alvo.id);
      setBusyId(null);
    }
  };

  /** Remove a linha só da tela; o backend não é chamado. */
  const handleDispensar = (row) => {
    setImports((previous) => previous.filter((item) => item.id !== row.id));
    setJaSalvas((previous) => {
      const next = { ...previous };
      delete next[row.id];
      return next;
    });
    setCount((c) => Math.max(0, c - 1));
  };

  /** Só ao confirmar no modal o efetivar é chamado de verdade. */
  const handleConfirmEfetivar = ({ phone, replaceCadastroPhone }) =>
    efetivarAlvo &&
    efetivarDocumento(efetivarAlvo, {
      phone,
      ...(replaceCadastroPhone === true ? { replaceCadastroPhone: true } : {})
    });

  /** "Sim, é este cliente": vincula a apólice ao contato que já existe. */
  const handleVincularContatoExistente = (linkContactId) =>
    efetivarAlvo &&
    efetivarDocumento(efetivarAlvo, {
      phone: conflito?.phone,
      linkExistingContact: true,
      // Com vários contatos em conflito o backend exige o id escolhido
      // (precisa estar em contactIds); com um só, é dispensado.
      ...(Number.isInteger(linkContactId) && linkContactId > 0 ? { linkContactId } : {})
    });

  /** "Não": efetiva sem telefone, como se o campo tivesse ficado em branco. */
  const handleEfetivarSemContato = () => efetivarAlvo && efetivarDocumento(efetivarAlvo, {});

  const handleRetryPassword = async (row) => {
    const password = (passwordDrafts[row.id] || "").trim();
    if (!password) return;

    setBusyId(row.id);
    try {
      const { data } = await api.post(`/policy-document-imports/${row.id}/retry-password`, {
        password,
      });
      replaceRow(data);
      setPasswordDrafts((previous) => ({ ...previous, [row.id]: "" }));
    } catch (err) {
      toastError(err);
    }
    setBusyId(null);
  };

  const canEfetivar = (row) => row.status === "pendente_conferencia" && !jaSalvas[row.id];
  const canIgnorar = (row) => row.status !== "efetivado" && row.status !== "ignorado";
  const canUnlock = (row) =>
    ["senha_pendente", "erro_leitura", "ignorado"].includes(row.status);

  return (
    <>
      {batchFilter ? (
        <Paper variant="outlined" className={classes.batchNotice}>
          <Typography variant="body2">
            Mostrando apenas os documentos do lote que você acabou de importar.
          </Typography>
          <Button size="small" onClick={onClearBatchFilter}>
            Ver todos
          </Button>
        </Paper>
      ) : null}

      <Paper className={classes.toolbar} variant="outlined">
        <TextField
          className={classes.search}
          size="small"
          variant="outlined"
          placeholder="Buscar por nome, CPF/CNPJ ou número da apólice"
          value={searchParam}
          onChange={(event) => handleSearchChange(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="disabled" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl variant="outlined" size="small" className={classes.statusFilter}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(event) => handleStatusChange(event.target.value)}
          >
            {STATUS_FILTERS.map((option) => (
              <MenuItem key={option.value || "todos"} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="body2" className={classes.count}>
          {count} documento(s)
        </Typography>
      </Paper>

      <Paper className={classes.tableWrapper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>CPF/CNPJ</TableCell>
              <TableCell>Produto</TableCell>
              <TableCell>Seguradora</TableCell>
              <TableCell>Vigência</TableCell>
              <TableCell align="right">Prêmio</TableCell>
              <TableCell>Nº apólice</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {imports.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.clientNameRaw || "—"}</TableCell>
                <TableCell>{row.cpfCnpj || "—"}</TableCell>
                <TableCell>{row.product || "—"}</TableCell>
                <TableCell>{row.insurer || "—"}</TableCell>
                <TableCell>
                  {row.startDate || row.endDate
                    ? `${formatDateOnly(row.startDate)} a ${formatDateOnly(row.endDate)}`
                    : "—"}
                </TableCell>
                <TableCell align="right">
                  {row.premium !== null && row.premium !== undefined
                    ? formatCurrency(row.premium)
                    : "—"}
                </TableCell>
                <TableCell>{row.policyNumber || "—"}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    className={classes.chip}
                    style={{ backgroundColor: statusColor(row.status) }}
                    label={statusLabel(row.status)}
                  />
                  {row.errorReason ? (
                    <div className={classes.reason}>{errorReasonLabel(row.errorReason)}</div>
                  ) : null}
                  {jaSalvas[row.id] ? (
                    <>
                      <Chip
                        size="small"
                        className={classes.chip}
                        color="primary"
                        label={i18n.t("policyImport.alreadyImported.badge")}
                      />
                      <div className={classes.reason}>
                        {i18n.t("policyImport.alreadyImported.message")}
                      </div>
                    </>
                  ) : null}
                  {(() => {
                    const preview = classifyMatchPreview(row);
                    if (preview.kind === "duplicata") {
                      return (
                        <>
                          <Chip
                            size="small"
                            className={classes.chip}
                            style={{ backgroundColor: "#ffb74d" }}
                            label={i18n.t("policyImport.possibleDuplicate.badge")}
                          />
                          <div className={classes.reason}>
                            {i18n.t("policyImport.possibleDuplicate.message", {
                              ids: formatIds(preview.ids),
                            })}
                          </div>
                        </>
                      );
                    }
                    if (preview.kind === "atualiza") {
                      return (
                        <div className={classes.reason}>
                          {i18n.t("policyImport.willUpdate.message", { id: preview.policyId })}
                        </div>
                      );
                    }
                    return null;
                  })()}
                  <div className={classes.reason}>
                    Importado em {formatDateOnly(row.createdAt)}
                  </div>
                </TableCell>
                <TableCell align="right">
                  <div className={classes.actions}>
                    {canEfetivar(row) ? (
                      <Tooltip title="Criar ou vincular a apólice e encerrar a conferência">
                        <span>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            className={classes.action}
                            disabled={busyId === row.id}
                            onClick={() => setEfetivarAlvo(row)}
                          >
                            {busyId === row.id ? (
                              <CircularProgress size={12} color="inherit" />
                            ) : (
                              "Efetivar"
                            )}
                          </Button>
                        </span>
                      </Tooltip>
                    ) : null}

                    {jaSalvas[row.id] ? (
                      <Tooltip title={i18n.t("policyImport.alreadyImported.dismissHint")}>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            className={classes.action}
                            onClick={() => handleDispensar(row)}
                          >
                            {i18n.t("policyImport.alreadyImported.dismiss")}
                          </Button>
                        </span>
                      </Tooltip>
                    ) : null}

                    {canIgnorar(row) ? (
                      <Button
                        size="small"
                        variant="outlined"
                        className={classes.action}
                        disabled={busyId === row.id}
                        onClick={() => runAction(row, "ignorar")}
                      >
                        Ignorar
                      </Button>
                    ) : null}

                    {canUnlock(row) ? (
                      <Tooltip title="Voltar o documento para pendente de conferência">
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            className={classes.action}
                            disabled={busyId === row.id}
                            onClick={() => runAction(row, "unlock")}
                          >
                            Desbloquear
                          </Button>
                        </span>
                      </Tooltip>
                    ) : null}
                  </div>

                  {row.status === "senha_pendente" ? (
                    <div className={classes.passwordBox}>
                      <TextField
                        size="small"
                        variant="outlined"
                        type="password"
                        label="Senha do PDF"
                        value={passwordDrafts[row.id] || ""}
                        disabled={busyId === row.id}
                        onChange={(event) =>
                          setPasswordDrafts((previous) => ({
                            ...previous,
                            [row.id]: event.target.value,
                          }))
                        }
                      />
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        className={classes.action}
                        disabled={busyId === row.id || !(passwordDrafts[row.id] || "").trim()}
                        onClick={() => handleRetryPassword(row)}
                      >
                        Reprocessar
                      </Button>
                    </div>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}

            {loading ? <TableRowSkeleton columns={COLUMNS} /> : null}

            {!loading && imports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS} align="center" style={{ padding: 32 }}>
                  <Typography variant="subtitle1">
                    Nenhum documento encontrado.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>

        {hasMore && !loading ? (
          <div style={{ textAlign: "center", padding: 8 }}>
            <Button onClick={() => setPageNumber((previous) => previous + 1)}>
              Carregar mais
            </Button>
          </div>
        ) : null}
      </Paper>

      <ConfirmEfetivarModal
        open={Boolean(efetivarAlvo)}
        documento={efetivarAlvo}
        saving={Boolean(efetivarAlvo) && busyId === efetivarAlvo.id}
        conflito={conflito}
        onClose={() => {
          setEfetivarAlvo(null);
          setConflito(null);
        }}
        onConfirm={handleConfirmEfetivar}
        onVincularExistente={handleVincularContatoExistente}
        onEfetivarSemContato={handleEfetivarSemContato}
      />
    </>
  );
};

export default ConferenceTab;
